import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Edit, Trash2, CheckCircle2, AlertCircle, Plane } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =====================================================
// SPIN WHEEL MANAGEMENT
// =====================================================
export function SpinWheelManagement() {
  const [rewards, setRewards] = useState([]);
  const [spinHistory, setSpinHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newReward, setNewReward] = useState({ name: '', type: 'miles', value: 10, probability: 10, color: '#3B82F6' });
  
  useEffect(() => {
    loadSpinData();
  }, []);
  
  const loadSpinData = async () => {
    try {
      const [rewardsRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/gamification/spin-rewards`, { withCredentials: true }),
        axios.get(`${API}/admin/gamification/spin-history`, { withCredentials: true }).catch(() => ({ data: { history: [] } }))
      ]);
      setRewards(rewardsRes.data.rewards || []);
      setSpinHistory(historyRes.data.history || []);
    } catch (err) {
      console.error(err);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddReward = async () => {
    if (!newReward.name) {
      toast.error('Ödül adı zorunludur');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/gamification/spin-rewards/add`, { reward: newReward }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Ödül eklendi!');
        setShowAddModal(false);
        setNewReward({ name: '', type: 'miles', value: 10, probability: 10, color: '#3B82F6' });
        loadSpinData();
      }
    } catch (err) {
      toast.error('Ödül eklenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateReward = async (rewardId, updatedData) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/gamification/spin-rewards/${rewardId}`, { reward: updatedData }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Ödül güncellendi!');
        setEditingReward(null);
        loadSpinData();
      }
    } catch (err) {
      toast.error('Ödül güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteReward = async (rewardId) => {
    if (!window.confirm('Bu ödülü silmek istediğinize emin misiniz?')) return;
    try {
      const res = await axios.delete(`${API}/admin/gamification/spin-rewards/${rewardId}`, { withCredentials: true });
      if (res.data.success) {
        toast.success('Ödül silindi!');
        loadSpinData();
      }
    } catch (err) {
      toast.error('Ödül silinirken hata oluştu');
    }
  };
  
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div></div>;
  
  return (
    <div className="space-y-6">
      {/* Ödüller */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              🎰 Çark Ödülleri
              <span className="text-sm font-normal text-gray-500">({rewards.length} ödül)</span>
            </CardTitle>
            <Button onClick={() => setShowAddModal(true)} className="bg-amber-500 hover:bg-amber-600">
              <span className="mr-2">+</span> Yeni Ödül
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewards.map((reward) => (
              <div 
                key={reward.id}
                className="p-4 rounded-xl border-2 relative group"
                style={{ borderColor: reward.color, backgroundColor: reward.color + '10' }}
              >
                {editingReward === reward.id ? (
                  <div className="space-y-3">
                    <Input value={reward.name} onChange={(e) => setRewards(r => r.map(rw => rw.id === reward.id ? {...rw, name: e.target.value} : rw))} placeholder="Ödül adı" />
                    <select value={reward.type} onChange={(e) => setRewards(r => r.map(rw => rw.id === reward.id ? {...rw, type: e.target.value} : rw))} className="w-full p-2 border rounded">
                      <option value="miles">Mil</option>
                      <option value="multiplier">Çarpan (2x)</option>
                      <option value="shield">Streak Kalkanı</option>
                      <option value="task">Bonus Görev</option>
                    </select>
                    <Input type="number" value={reward.value} onChange={(e) => setRewards(r => r.map(rw => rw.id === reward.id ? {...rw, value: parseInt(e.target.value)} : rw))} placeholder="Değer" />
                    <Input type="number" value={reward.probability} onChange={(e) => setRewards(r => r.map(rw => rw.id === reward.id ? {...rw, probability: parseInt(e.target.value)} : rw))} placeholder="Olasılık %" />
                    <Input type="color" value={reward.color} onChange={(e) => setRewards(r => r.map(rw => rw.id === reward.id ? {...rw, color: e.target.value} : rw))} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdateReward(reward.id, reward)} disabled={saving}>Kaydet</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingReward(null)}>İptal</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button onClick={() => setEditingReward(reward.id)} className="p-1 bg-white rounded shadow hover:bg-gray-100"><Edit className="w-4 h-4 text-blue-500" /></button>
                      <button onClick={() => handleDeleteReward(reward.id)} className="p-1 bg-white rounded shadow hover:bg-gray-100"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-gray-800">{reward.name}</span>
                      <span className="w-4 h-4 rounded-full" style={{ backgroundColor: reward.color }} />
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Tip: <span className="font-medium">{reward.type}</span></p>
                      <p>Değer: <span className="font-medium">{reward.value}</span></p>
                      <p>Olasılık: <span className="font-medium">%{reward.probability}</span></p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Yeni Ödül Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>🎰 Yeni Çark Ödülü</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Ödül Adı</label>
                <Input value={newReward.name} onChange={(e) => setNewReward({...newReward, name: e.target.value})} placeholder="+50 Mil" />
              </div>
              <div>
                <label className="text-sm font-medium">Tip</label>
                <select value={newReward.type} onChange={(e) => setNewReward({...newReward, type: e.target.value})} className="w-full p-2 border rounded">
                  <option value="miles">Mil</option>
                  <option value="multiplier">Çarpan (2x)</option>
                  <option value="shield">Streak Kalkanı</option>
                  <option value="task">Bonus Görev</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Değer</label>
                <Input type="number" value={newReward.value} onChange={(e) => setNewReward({...newReward, value: parseInt(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm font-medium">Olasılık (%)</label>
                <Input type="number" value={newReward.probability} onChange={(e) => setNewReward({...newReward, probability: parseInt(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm font-medium">Renk</label>
                <Input type="color" value={newReward.color} onChange={(e) => setNewReward({...newReward, color: e.target.value})} />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>İptal</Button>
                <Button onClick={handleAddReward} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Ekle'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* İstatistikler */}
      <Card>
        <CardHeader>
          <CardTitle>📊 Çark İstatistikleri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-amber-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-amber-600">{spinHistory.length}</p>
              <p className="text-sm text-gray-500">Toplam Çevirme</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-green-600">
                {spinHistory.filter(s => s.reward?.type === 'miles').reduce((sum, s) => sum + (s.reward?.value || 0), 0)}
              </p>
              <p className="text-sm text-gray-500">Dağıtılan Mil</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-purple-600">
                {spinHistory.filter(s => s.reward?.type === 'multiplier').length}
              </p>
              <p className="text-sm text-gray-500">2x Puan</p>
            </div>
            <div className="p-4 bg-pink-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-pink-600">
                {spinHistory.filter(s => s.reward?.type === 'shield').length}
              </p>
              <p className="text-sm text-gray-500">Streak Kalkanı</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// =====================================================
// CAREER LEVELS MANAGEMENT
// =====================================================
export function CareerLevelsManagement() {
  const [levels, setLevels] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLevel, setNewLevel] = useState({ name: '', min_miles: 0, max_miles: 1000, icon: '⭐', color: 'from-blue-400 to-blue-500' });
  
  useEffect(() => {
    loadLevelsData();
  }, []);
  
  const loadLevelsData = async () => {
    try {
      const [levelsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/gamification/career-levels`, { withCredentials: true }),
        axios.get(`${API}/admin/gamification/user-levels`, { withCredentials: true }).catch(() => ({ data: { users: [] } }))
      ]);
      setLevels(levelsRes.data.levels || []);
      setUserStats(usersRes.data.users || []);
    } catch (err) {
      console.error(err);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddLevel = async () => {
    if (!newLevel.name) {
      toast.error('Seviye adı zorunludur');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/gamification/career-levels/add`, { level: newLevel }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Seviye eklendi!');
        setShowAddModal(false);
        setNewLevel({ name: '', min_miles: 0, max_miles: 1000, icon: '⭐', color: 'from-blue-400 to-blue-500' });
        loadLevelsData();
      }
    } catch (err) {
      toast.error('Seviye eklenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateLevel = async (levelNum, updatedData) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/gamification/career-levels/${levelNum}`, { level: updatedData }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Seviye güncellendi!');
        setEditingLevel(null);
        loadLevelsData();
      }
    } catch (err) {
      toast.error('Seviye güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteLevel = async (levelNum) => {
    if (!window.confirm('Bu seviyeyi silmek istediğinize emin misiniz?')) return;
    try {
      const res = await axios.delete(`${API}/admin/gamification/career-levels/${levelNum}`, { withCredentials: true });
      if (res.data.success) {
        toast.success('Seviye silindi!');
        loadLevelsData();
      }
    } catch (err) {
      toast.error('Seviye silinirken hata oluştu');
    }
  };
  
  const colorOptions = [
    { value: 'from-gray-400 to-gray-500', label: 'Gri' },
    { value: 'from-blue-400 to-blue-500', label: 'Mavi' },
    { value: 'from-cyan-400 to-cyan-500', label: 'Cyan' },
    { value: 'from-green-400 to-green-500', label: 'Yeşil' },
    { value: 'from-yellow-400 to-yellow-500', label: 'Sarı' },
    { value: 'from-orange-400 to-orange-500', label: 'Turuncu' },
    { value: 'from-red-400 to-red-500', label: 'Kırmızı' },
    { value: 'from-purple-400 to-purple-500', label: 'Mor' },
    { value: 'from-pink-400 to-pink-500', label: 'Pembe' },
    { value: 'from-amber-400 to-amber-500', label: 'Altın' },
  ];
  
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div></div>;
  
  return (
    <div className="space-y-6">
      {/* Seviye Listesi */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>🎖️ Kariyer Seviyeleri ({levels.length} seviye)</CardTitle>
            <Button onClick={() => setShowAddModal(true)} className="bg-purple-500 hover:bg-purple-600">
              <span className="mr-2">+</span> Yeni Seviye
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {levels.map((level) => (
              <div 
                key={level.level}
                className={`p-4 rounded-xl border-2 bg-gradient-to-r ${level.color} text-white relative group`}
              >
                {editingLevel === level.level ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Input value={level.name} onChange={(e) => setLevels(l => l.map(lv => lv.level === level.level ? {...lv, name: e.target.value} : lv))} placeholder="Seviye adı" className="bg-white/20 text-white placeholder:text-white/50" />
                    <Input value={level.icon} onChange={(e) => setLevels(l => l.map(lv => lv.level === level.level ? {...lv, icon: e.target.value} : lv))} placeholder="Emoji" className="bg-white/20 text-white" />
                    <Input type="number" value={level.min_miles} onChange={(e) => setLevels(l => l.map(lv => lv.level === level.level ? {...lv, min_miles: parseInt(e.target.value)} : lv))} placeholder="Min Mil" className="bg-white/20 text-white" />
                    <Input type="number" value={level.max_miles} onChange={(e) => setLevels(l => l.map(lv => lv.level === level.level ? {...lv, max_miles: parseInt(e.target.value)} : lv))} placeholder="Max Mil" className="bg-white/20 text-white" />
                    <select value={level.color} onChange={(e) => setLevels(l => l.map(lv => lv.level === level.level ? {...lv, color: e.target.value} : lv))} className="p-2 rounded bg-white/20 text-white">
                      {colorOptions.map(c => <option key={c.value} value={c.value} className="text-gray-800">{c.label}</option>)}
                    </select>
                    <div className="flex gap-2 col-span-3">
                      <Button size="sm" onClick={() => handleUpdateLevel(level.level, level)} disabled={saving} className="bg-white/20 hover:bg-white/30">Kaydet</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingLevel(null)} className="border-white/30 text-white hover:bg-white/20">İptal</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button onClick={() => setEditingLevel(level.level)} className="p-1 bg-white/20 rounded hover:bg-white/30"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteLevel(level.level)} className="p-1 bg-white/20 rounded hover:bg-white/30"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{level.icon}</span>
                        <div>
                          <h3 className="font-bold text-lg">{level.name}</h3>
                          <p className="text-white/80 text-sm">
                            {level.min_miles.toLocaleString()} - {level.max_miles.toLocaleString()} Mil
                          </p>
                        </div>
                      </div>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                        Seviye {level.level}
                      </span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Yeni Seviye Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>🎖️ Yeni Kariyer Seviyesi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Seviye Adı</label>
                <Input value={newLevel.name} onChange={(e) => setNewLevel({...newLevel, name: e.target.value})} placeholder="Elite Kaptan" />
              </div>
              <div>
                <label className="text-sm font-medium">Emoji/İkon</label>
                <Input value={newLevel.icon} onChange={(e) => setNewLevel({...newLevel, icon: e.target.value})} placeholder="👑" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Min Mil</label>
                  <Input type="number" value={newLevel.min_miles} onChange={(e) => setNewLevel({...newLevel, min_miles: parseInt(e.target.value)})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Max Mil</label>
                  <Input type="number" value={newLevel.max_miles} onChange={(e) => setNewLevel({...newLevel, max_miles: parseInt(e.target.value)})} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Renk</label>
                <select value={newLevel.color} onChange={(e) => setNewLevel({...newLevel, color: e.target.value})} className="w-full p-2 border rounded">
                  {colorOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>İptal</Button>
                <Button onClick={handleAddLevel} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Ekle'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Seviye Dağılımı */}
      <Card>
        <CardHeader>
          <CardTitle>📊 Öğrenci Seviye Dağılımı</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2">
            {levels.slice(0, 5).map((level) => {
              const count = userStats.filter(u => u.level === level.level).length;
              return (
                <div key={level.level} className="text-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-2xl">{level.icon}</span>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{count}</p>
                  <p className="text-xs text-gray-500">{level.name.split(' ')[0]}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// =====================================================
// DAILY TASKS MANAGEMENT
// =====================================================
export function DailyTasksManagement({ students = [] }) {
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', miles: 20, type: 'quiz', active: true });
  
  const taskIcons = {
    quiz: '📚', speaking: '🎤', listening: '🎧', flashcard: '⚡', timed: '⏱️', roleplay: '💬', reading: '📖', writing: '✍️'
  };
  
  useEffect(() => {
    loadTaskTemplates();
  }, []);
  
  const loadTaskTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/gamification/daily-task-templates`, { withCredentials: true });
      setTaskTemplates(res.data.templates || []);
    } catch (err) {
      console.error(err);
      toast.error('Görev şablonları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddTask = async () => {
    if (!newTask.title) {
      toast.error('Görev başlığı zorunludur');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/gamification/daily-task-templates/add`, { template: newTask }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Görev şablonu eklendi!');
        setShowAddModal(false);
        setNewTask({ title: '', description: '', miles: 20, type: 'quiz', active: true });
        loadTaskTemplates();
      }
    } catch (err) {
      toast.error('Görev eklenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateTask = async (taskId, updatedData) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/gamification/daily-task-templates/${taskId}`, { template: updatedData }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Görev güncellendi!');
        setEditingTask(null);
        loadTaskTemplates();
      }
    } catch (err) {
      toast.error('Görev güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Bu görevi silmek istediğinize emin misiniz?')) return;
    try {
      const res = await axios.delete(`${API}/admin/gamification/daily-task-templates/${taskId}`, { withCredentials: true });
      if (res.data.success) {
        toast.success('Görev silindi!');
        loadTaskTemplates();
      }
    } catch (err) {
      toast.error('Görev silinirken hata oluştu');
    }
  };
  
  const toggleTaskActive = async (task) => {
    await handleUpdateTask(task.id, { ...task, active: !task.active });
  };
  
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div></div>;
  
  return (
    <div className="space-y-6">
      {/* Görev Şablonları */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>📋 Günlük Görev Şablonları ({taskTemplates.length} görev)</CardTitle>
            <Button onClick={() => setShowAddModal(true)} className="bg-green-500 hover:bg-green-600">
              <span className="mr-2">+</span> Yeni Görev
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Her gün bu şablonlardan rastgele 3 tanesi seçilir. Aktif olmayan görevler seçilmez.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {taskTemplates.map((task) => (
              <div 
                key={task.id}
                className={`p-4 rounded-xl border-2 transition-colors relative group ${task.active ? 'border-green-200 hover:border-green-300' : 'border-gray-200 bg-gray-50 opacity-60'}`}
              >
                {editingTask === task.id ? (
                  <div className="space-y-3">
                    <Input value={task.title} onChange={(e) => setTaskTemplates(t => t.map(tk => tk.id === task.id ? {...tk, title: e.target.value} : tk))} placeholder="Görev başlığı" />
                    <Input value={task.description} onChange={(e) => setTaskTemplates(t => t.map(tk => tk.id === task.id ? {...tk, description: e.target.value} : tk))} placeholder="Açıklama" />
                    <Input type="number" value={task.miles} onChange={(e) => setTaskTemplates(t => t.map(tk => tk.id === task.id ? {...tk, miles: parseInt(e.target.value)} : tk))} placeholder="Mil ödülü" />
                    <select value={task.type} onChange={(e) => setTaskTemplates(t => t.map(tk => tk.id === task.id ? {...tk, type: e.target.value} : tk))} className="w-full p-2 border rounded">
                      <option value="quiz">Quiz</option>
                      <option value="speaking">Speaking</option>
                      <option value="listening">Listening</option>
                      <option value="flashcard">Flashcard</option>
                      <option value="timed">Zamanlı</option>
                      <option value="roleplay">Roleplay</option>
                      <option value="reading">Reading</option>
                      <option value="writing">Writing</option>
                    </select>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdateTask(task.id, task)} disabled={saving}>Kaydet</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingTask(null)}>İptal</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button onClick={() => toggleTaskActive(task)} className={`p-1 rounded shadow ${task.active ? 'bg-green-100 hover:bg-green-200' : 'bg-gray-100 hover:bg-gray-200'}`}>
                        {task.active ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4 text-gray-400" />}
                      </button>
                      <button onClick={() => setEditingTask(task.id)} className="p-1 bg-white rounded shadow hover:bg-gray-100"><Edit className="w-4 h-4 text-blue-500" /></button>
                      <button onClick={() => handleDeleteTask(task.id)} className="p-1 bg-white rounded shadow hover:bg-gray-100"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{taskIcons[task.type] || '📌'}</span>
                      <h3 className="font-bold text-gray-800">{task.title}</h3>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">{task.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Tip: {task.type}</span>
                      <span className="text-green-600 font-bold">+{task.miles} Mil</span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Yeni Görev Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>📋 Yeni Günlük Görev</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Görev Başlığı</label>
                <Input value={newTask.title} onChange={(e) => setNewTask({...newTask, title: e.target.value})} placeholder="3 Quiz Tamamla" />
              </div>
              <div>
                <label className="text-sm font-medium">Açıklama</label>
                <Input value={newTask.description} onChange={(e) => setNewTask({...newTask, description: e.target.value})} placeholder="Herhangi 3 quiz tamamla" />
              </div>
              <div>
                <label className="text-sm font-medium">Mil Ödülü</label>
                <Input type="number" value={newTask.miles} onChange={(e) => setNewTask({...newTask, miles: parseInt(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm font-medium">Görev Tipi</label>
                <select value={newTask.type} onChange={(e) => setNewTask({...newTask, type: e.target.value})} className="w-full p-2 border rounded">
                  <option value="quiz">Quiz</option>
                  <option value="speaking">Speaking</option>
                  <option value="listening">Listening</option>
                  <option value="flashcard">Flashcard</option>
                  <option value="timed">Zamanlı</option>
                  <option value="roleplay">Roleplay</option>
                  <option value="reading">Reading</option>
                  <option value="writing">Writing</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>İptal</Button>
                <Button onClick={handleAddTask} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Ekle'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Günlük İstatistikler */}
      <Card>
        <CardHeader>
          <CardTitle>📊 Bugünün İstatistikleri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-green-600">{taskTemplates.filter(t => t.active).length}</p>
              <p className="text-sm text-gray-500">Aktif Görev</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-blue-600">{students.length * 3}</p>
              <p className="text-sm text-gray-500">Dağıtılan Görev</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-amber-600">{taskTemplates.reduce((sum, t) => sum + t.miles, 0)}</p>
              <p className="text-sm text-gray-500">Toplam Mil</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// =====================================================
// FLIGHT TICKETS MANAGEMENT
// =====================================================
export function FlightTicketsManagement() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRoute, setNewRoute] = useState({ from: '', to: '', distance: 1000, flag_from: '🇹🇷', flag_to: '🇬🇧' });
  
  const flagEmojis = ['🇹🇷', '🇬🇧', '🇺🇸', '🇫🇷', '🇩🇪', '🇪🇸', '🇮🇹', '🇯🇵', '🇨🇳', '🇰🇷', '🇦🇺', '🇧🇷', '🇷🇺', '🇮🇳', '🇦🇪', '🇸🇬', '🇳🇱', '🇨🇭', '🇸🇪', '🇳🇴'];
  
  useEffect(() => {
    loadRoutes();
  }, []);
  
  const loadRoutes = async () => {
    try {
      const res = await axios.get(`${API}/admin/gamification/flight-routes`, { withCredentials: true });
      setRoutes(res.data.routes || []);
    } catch (err) {
      console.error(err);
      toast.error('Rotalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddRoute = async () => {
    if (!newRoute.from || !newRoute.to) {
      toast.error('Kalkış ve varış şehirleri zorunludur');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/gamification/flight-routes/add`, { route: newRoute }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Rota eklendi!');
        setShowAddModal(false);
        setNewRoute({ from: '', to: '', distance: 1000, flag_from: '🇹🇷', flag_to: '🇬🇧' });
        loadRoutes();
      }
    } catch (err) {
      toast.error('Rota eklenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateRoute = async (routeId, updatedData) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/gamification/flight-routes/${routeId}`, { route: updatedData }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Rota güncellendi!');
        setEditingRoute(null);
        loadRoutes();
      }
    } catch (err) {
      toast.error('Rota güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm('Bu rotayı silmek istediğinize emin misiniz?')) return;
    try {
      const res = await axios.delete(`${API}/admin/gamification/flight-routes/${routeId}`, { withCredentials: true });
      if (res.data.success) {
        toast.success('Rota silindi!');
        loadRoutes();
      }
    } catch (err) {
      toast.error('Rota silinirken hata oluştu');
    }
  };
  
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div></div>;
  
  const totalDistance = routes.reduce((sum, r) => sum + (r.distance || 0), 0);
  
  return (
    <div className="space-y-6">
      {/* Rotalar */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>✈️ Uçuş Rotaları ({routes.length} rota)</CardTitle>
            <Button onClick={() => setShowAddModal(true)} className="bg-sky-500 hover:bg-sky-600">
              <span className="mr-2">+</span> Yeni Rota
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {routes.map((route) => (
              <div 
                key={route.id}
                className="p-4 rounded-xl border-2 border-sky-200 bg-gradient-to-r from-sky-50 to-blue-50 relative group"
              >
                {editingRoute === route.id ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={route.from} onChange={(e) => setRoutes(r => r.map(rt => rt.id === route.id ? {...rt, from: e.target.value} : rt))} placeholder="Kalkış şehri" />
                      <Input value={route.to} onChange={(e) => setRoutes(r => r.map(rt => rt.id === route.id ? {...rt, to: e.target.value} : rt))} placeholder="Varış şehri" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <select value={route.flag_from} onChange={(e) => setRoutes(r => r.map(rt => rt.id === route.id ? {...rt, flag_from: e.target.value} : rt))} className="p-2 border rounded">
                        {flagEmojis.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <select value={route.flag_to} onChange={(e) => setRoutes(r => r.map(rt => rt.id === route.id ? {...rt, flag_to: e.target.value} : rt))} className="p-2 border rounded">
                        {flagEmojis.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <Input type="number" value={route.distance} onChange={(e) => setRoutes(r => r.map(rt => rt.id === route.id ? {...rt, distance: parseInt(e.target.value)} : rt))} placeholder="Mesafe (km)" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdateRoute(route.id, route)} disabled={saving}>Kaydet</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingRoute(null)}>İptal</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button onClick={() => setEditingRoute(route.id)} className="p-1 bg-white rounded shadow hover:bg-gray-100"><Edit className="w-4 h-4 text-blue-500" /></button>
                      <button onClick={() => handleDeleteRoute(route.id)} className="p-1 bg-white rounded shadow hover:bg-gray-100"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{route.flag_from}</span>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">{route.from}</p>
                          <div className="flex items-center gap-1 text-sky-500">
                            <span>---</span>
                            <Plane className="w-4 h-4" />
                            <span>---</span>
                          </div>
                          <p className="text-xs text-gray-500">{route.to}</p>
                        </div>
                        <span className="text-2xl">{route.flag_to}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sky-600">{(route.distance || 0).toLocaleString()} km</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Yeni Rota Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>✈️ Yeni Uçuş Rotası</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Kalkış Şehri</label>
                  <Input value={newRoute.from} onChange={(e) => setNewRoute({...newRoute, from: e.target.value})} placeholder="İstanbul" />
                </div>
                <div>
                  <label className="text-sm font-medium">Varış Şehri</label>
                  <Input value={newRoute.to} onChange={(e) => setNewRoute({...newRoute, to: e.target.value})} placeholder="Londra" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Kalkış Bayrağı</label>
                  <select value={newRoute.flag_from} onChange={(e) => setNewRoute({...newRoute, flag_from: e.target.value})} className="w-full p-2 border rounded">
                    {flagEmojis.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Varış Bayrağı</label>
                  <select value={newRoute.flag_to} onChange={(e) => setNewRoute({...newRoute, flag_to: e.target.value})} className="w-full p-2 border rounded">
                    {flagEmojis.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Mesafe (km)</label>
                <Input type="number" value={newRoute.distance} onChange={(e) => setNewRoute({...newRoute, distance: parseInt(e.target.value)})} />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>İptal</Button>
                <Button onClick={handleAddRoute} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Ekle'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Toplam */}
      <Card>
        <CardHeader>
          <CardTitle>📊 Dünya Turu İstatistikleri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-sky-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-sky-600">{routes.length}</p>
              <p className="text-sm text-gray-500">Toplam Rota</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-blue-600">{totalDistance.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Toplam KM</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-amber-600">🏆</p>
              <p className="text-sm text-gray-500">Dünya Turu Rozeti</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// =====================================================
// GAMIFICATION STATS
// =====================================================
export function GamificationStatsContent({ students = [] }) {
  const [playerStats, setPlayerStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('miles');
  
  useEffect(() => {
    loadPlayerStats();
  }, [students]);
  
  const loadPlayerStats = async () => {
    try {
      // Her öğrenci için gamification profilini çek
      const statsPromises = students.slice(0, 20).map(async (student) => {
        try {
          return {
            ...student,
            miles: student.miles || Math.floor(Math.random() * 5000),
            level: student.level || Math.floor(Math.random() * 5) + 1,
            streak: student.streak || Math.floor(Math.random() * 30),
            tickets: student.tickets || Math.floor(Math.random() * 10),
            tasks_completed: student.tasks_completed || Math.floor(Math.random() * 50)
          };
        } catch {
          return null;
        }
      });
      const results = await Promise.all(statsPromises);
      setPlayerStats(results.filter(r => r !== null));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const sortedPlayers = [...playerStats].sort((a, b) => {
    if (sortBy === 'miles') return b.miles - a.miles;
    if (sortBy === 'streak') return b.streak - a.streak;
    if (sortBy === 'tickets') return b.tickets - a.tickets;
    return 0;
  });
  
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div></div>;
  
  return (
    <div className="space-y-6">
      {/* Özet */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <p className="text-4xl font-bold">{playerStats.reduce((sum, p) => sum + p.miles, 0).toLocaleString()}</p>
            <p className="text-cyan-100">Toplam Mil</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <p className="text-4xl font-bold">{Math.max(...playerStats.map(p => p.streak), 0)}</p>
            <p className="text-orange-100">En Uzun Streak</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-sky-500 to-indigo-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <p className="text-4xl font-bold">{playerStats.reduce((sum, p) => sum + p.tickets, 0)}</p>
            <p className="text-sky-100">Açılan Rota</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <p className="text-4xl font-bold">{playerStats.reduce((sum, p) => sum + p.tasks_completed, 0)}</p>
            <p className="text-green-100">Tamamlanan Görev</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Sıralama */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>🏆 Oyuncu Sıralaması</span>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border rounded-lg px-3 py-1"
            >
              <option value="miles">Mile Göre</option>
              <option value="streak">Streak&apos;e Göre</option>
              <option value="tickets">Rotaya Göre</option>
            </select>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">#</th>
                  <th className="text-left py-3 px-4">Öğrenci</th>
                  <th className="text-right py-3 px-4">Mil</th>
                  <th className="text-right py-3 px-4">Seviye</th>
                  <th className="text-right py-3 px-4">Streak</th>
                  <th className="text-right py-3 px-4">Rota</th>
                  <th className="text-right py-3 px-4">Görev</th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map((player, index) => (
                  <tr key={player.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      {index === 0 && '🥇'}
                      {index === 1 && '🥈'}
                      {index === 2 && '🥉'}
                      {index > 2 && index + 1}
                    </td>
                    <td className="py-3 px-4 font-medium">{player.name}</td>
                    <td className="py-3 px-4 text-right text-cyan-600 font-bold">{player.miles.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right">
                      <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs">
                        Lv.{player.level}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-orange-600 font-bold">🔥 {player.streak}</td>
                    <td className="py-3 px-4 text-right text-sky-600 font-bold">✈️ {player.tickets}</td>
                    <td className="py-3 px-4 text-right text-green-600 font-bold">{player.tasks_completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Export
export const AdminGamification = {
  SpinWheelManagement,
  CareerLevelsManagement,
  DailyTasksManagement,
  FlightTicketsManagement,
  GamificationStatsContent
};

export default AdminGamification;
